import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Users, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Mail, 
  Phone, 
  MapPin,
  User as UserIcon,
  Building2,
  FileText,
  FolderPlus,
  Eye
} from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import PhoneInput from '../../components/UI/PhoneInput';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Customer } from '../../types';

function formatAddress(addr: Customer['address']): string {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  const parts = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean);
  return parts.join(', ') || '';
}

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    // Contact Person Information
    name: '',
    designation: '',
    phone: '',
    email: '',
    // Customer Information
    company: '',
    companyPhone: '',
    companyEmail: '',
    address: '',
    // TAX & VAT Information
    taxNumber: '',
    bin: '',
    // Projects
    projects: [] as Array<{ name: string; address: string }>,
  });
  const [submitting, setSubmitting] = useState(false);
  const [addProjectModalOpen, setAddProjectModalOpen] = useState(false);
  const [addProjectCustomer, setAddProjectCustomer] = useState<Customer | null>(null);
  const [addProjectName, setAddProjectName] = useState('');
  const [addProjectAddress, setAddProjectAddress] = useState('');
  const [addingProject, setAddingProject] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllCustomers();
      const list = Array.isArray(response.data) ? response.data : (response.data?.customers || []);
      setCustomers(list);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        // Contact Person Information
        name: customer.name || '',
        designation: customer.designation || '',
        phone: customer.phone || '',
        email: customer.email || '',
        // Customer Information
        company: customer.company || '',
        companyPhone: customer.companyPhone || '',
        companyEmail: customer.companyEmail || '',
        address: typeof customer.address === 'string' ? customer.address : '',
        // TAX & VAT Information
        taxNumber: customer.taxNumber || customer.taxId || '',
        bin: customer.bin || '',
        // Projects
        projects: customer.projects || [],
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        // Contact Person Information
        name: '',
        designation: '',
        phone: '',
        email: '',
        // Customer Information
        company: '',
        companyPhone: '',
        companyEmail: '',
        address: '',
        // TAX & VAT Information
        taxNumber: '',
        bin: '',
        // Projects
        projects: [],
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCustomer(null);
    setFormData({
      // Contact Person Information
      name: '',
      designation: '',
      phone: '',
      email: '',
      // Customer Information
      company: '',
      companyPhone: '',
      companyEmail: '',
      address: '',
      // TAX & VAT Information
      taxNumber: '',
      bin: '',
      // Projects
      projects: [],
    });
  };

  const handleAddProject = () => {
    setFormData({
      ...formData,
      projects: [...formData.projects, { name: '', address: '' }],
    });
  };

  const handleRemoveProject = (index: number) => {
    const newProjects = formData.projects.filter((_, i) => i !== index);
    setFormData({ ...formData, projects: newProjects });
  };

  const handleProjectChange = (index: number, field: 'name' | 'address', value: string) => {
    const newProjects = [...formData.projects];
    newProjects[index][field] = value;
    setFormData({ ...formData, projects: newProjects });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    try {
      setSubmitting(true);
      if (editingCustomer) {
        await apiService.updateCustomer(editingCustomer._id, formData);
        toast.success('Customer updated successfully');
      } else {
        await apiService.createCustomer(formData);
        toast.success('Customer created successfully');
      }
      handleCloseModal();
      fetchCustomers();
    } catch (error: any) {
      console.error('Error saving customer:', error);
      toast.error(error.response?.data?.message || 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await apiService.deleteCustomer(id);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.message || 'Failed to delete customer');
    }
  };

  const openAddProjectModal = (customer: Customer) => {
    setAddProjectCustomer(customer);
    setAddProjectName('');
    setAddProjectAddress('');
    setAddProjectModalOpen(true);
  };

  const handleOpenViewModal = (customer: Customer) => {
    setViewingCustomer(customer);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingCustomer(null);
  };

  const closeAddProjectModal = () => {
    setAddProjectModalOpen(false);
    setAddProjectCustomer(null);
    setAddProjectName('');
    setAddProjectAddress('');
  };

  const handleAddProjectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addProjectCustomer) return;
    if (!addProjectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    try {
      setAddingProject(true);
      const existingProjects = addProjectCustomer.projects || [];
      await apiService.updateCustomer(addProjectCustomer._id, {
        ...addProjectCustomer,
        projects: [...existingProjects, { name: addProjectName.trim(), address: addProjectAddress.trim() }],
      });
      toast.success('Project added successfully');
      closeAddProjectModal();
      fetchCustomers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add project');
    } finally {
      setAddingProject(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            Customers
          </h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
            Manage your customers and their information
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Button
            icon={<Plus className="w-4 h-4" />}
            onClick={() => handleOpenModal()}
          >
            Add Customer
          </Button>
        </motion.div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card padding="md">
          <Input
            placeholder="Search customers by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="w-5 h-5" />}
            fullWidth
          />
        </Card>
      </motion.div>

      {/* Customers Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <Card padding="md">
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          </Card>
        ) : filteredCustomers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card padding="md" className="h-full flex flex-col overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-500 -mx-5 -mt-5 mb-4" />
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-900 truncate" style={{ fontFamily: 'var(--font-family)' }}>
                        {customer.name}
                      </h3>
                      {customer.company && (
                        <p className="text-sm text-gray-500 truncate">{customer.company}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3 text-sm flex-1">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Contact</p>
                      <div className="space-y-1">
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                        {customer.email && (
                          <div className="flex items-center gap-2 text-gray-700 truncate">
                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{customer.email}</span>
                          </div>
                        )}
                        {!customer.phone && !customer.email && (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Address</p>
                      <p className="text-gray-600 text-xs line-clamp-2">
                        {formatAddress(customer.address) || (typeof customer.address === 'string' ? customer.address : '') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Projects</p>
                      {(customer.projects && customer.projects.length > 0) ? (
                        <ul className="space-y-1.5 text-xs text-gray-600">
                          {customer.projects.map((proj, i) => (
                            <li key={i} className="pl-0">
                              <span className="font-medium text-gray-700">{proj.name || 'Unnamed'}</span>
                              {proj.address && <span className="block text-gray-500 mt-0.5">{proj.address}</span>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No projects</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:bg-blue-50"
                      icon={<Eye className="w-4 h-4" />}
                      onClick={() => handleOpenViewModal(customer)}
                      title="View"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-600 hover:bg-amber-50"
                      icon={<Edit2 className="w-4 h-4" />}
                      onClick={() => handleOpenModal(customer)}
                      title="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-emerald-600 hover:bg-emerald-50"
                      icon={<FolderPlus className="w-4 h-4" />}
                      onClick={() => openAddProjectModal(customer)}
                      title="Add Project"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => handleDelete(customer._id)}
                      title="Delete"
                    />
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card padding="lg">
            <div className="px-6 py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No customers yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by adding your first customer.
              </p>
              <div className="mt-6">
                <Button
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => handleOpenModal()}
                >
                  Add Customer
                </Button>
              </div>
            </div>
          </Card>
        )}
      </motion.div>

      {/* View Customer Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        title={viewingCustomer ? `Customer – ${viewingCustomer.name}` : 'Customer details'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCloseViewModal}>
              Close
            </Button>
            {viewingCustomer && (
              <Button
                icon={<Edit2 className="w-4 h-4" />}
                onClick={() => {
                  handleCloseViewModal();
                  handleOpenModal(viewingCustomer);
                }}
              >
                Edit
              </Button>
            )}
          </div>
        }
      >
        {viewingCustomer && (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <UserIcon className="w-4 h-4" /> Contact person
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><dt className="text-gray-500">Name</dt><dd className="font-medium text-gray-900">{viewingCustomer.name}</dd></div>
                {viewingCustomer.designation && <div><dt className="text-gray-500">Designation</dt><dd className="text-gray-900">{viewingCustomer.designation}</dd></div>}
                {viewingCustomer.phone && <div><dt className="text-gray-500">Phone</dt><dd className="text-gray-900">{viewingCustomer.phone}</dd></div>}
                {viewingCustomer.email && <div><dt className="text-gray-500">Email</dt><dd className="text-gray-900">{viewingCustomer.email}</dd></div>}
              </dl>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Company
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {viewingCustomer.company && <div><dt className="text-gray-500">Company</dt><dd className="font-medium text-gray-900">{viewingCustomer.company}</dd></div>}
                {(viewingCustomer.companyPhone || viewingCustomer.companyEmail) && (
                  <>
                    {viewingCustomer.companyPhone && <div><dt className="text-gray-500">Company phone</dt><dd className="text-gray-900">{viewingCustomer.companyPhone}</dd></div>}
                    {viewingCustomer.companyEmail && <div><dt className="text-gray-500">Company email</dt><dd className="text-gray-900">{viewingCustomer.companyEmail}</dd></div>}
                  </>
                )}
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Address</dt>
                  <dd className="text-gray-900">{formatAddress(viewingCustomer.address) || (typeof viewingCustomer.address === 'string' ? viewingCustomer.address : '') || '—'}</dd>
                </div>
              </dl>
            </div>
            {(viewingCustomer.taxNumber || viewingCustomer.taxId || viewingCustomer.bin) && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> TAX & VAT
                </h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {(viewingCustomer.taxNumber || viewingCustomer.taxId) && <div><dt className="text-gray-500">TIN No.</dt><dd className="text-gray-900">{viewingCustomer.taxNumber || viewingCustomer.taxId}</dd></div>}
                  {viewingCustomer.bin && <div><dt className="text-gray-500">BIN No.</dt><dd className="text-gray-900">{viewingCustomer.bin}</dd></div>}
                </dl>
              </div>
            )}
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h4 className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <FolderPlus className="w-4 h-4" /> Projects
              </h4>
              {(viewingCustomer.projects && viewingCustomer.projects.length > 0) ? (
                <ul className="space-y-3">
                  {viewingCustomer.projects.map((proj, i) => (
                    <li key={i} className="bg-white rounded p-3 border border-orange-200">
                      <p className="font-medium text-gray-900">{proj.name || 'Unnamed'}</p>
                      {proj.address && <p className="text-sm text-gray-600 mt-1">{proj.address}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">No projects</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Add Project Modal */}
      <Modal
        isOpen={addProjectModalOpen}
        onClose={closeAddProjectModal}
        title={addProjectCustomer ? `Add Project – ${addProjectCustomer.name}` : 'Add Project'}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeAddProjectModal} disabled={addingProject}>
              Cancel
            </Button>
            <Button onClick={handleAddProjectSubmit} loading={addingProject} disabled={addingProject}>
              Add Project
            </Button>
          </div>
        }
      >
        <form onSubmit={handleAddProjectSubmit} className="space-y-4">
          <Input
            label="Project Name *"
            value={addProjectName}
            onChange={(e) => setAddProjectName(e.target.value)}
            placeholder="Enter project name"
            fullWidth
          />
          <Input
            label="Project Address"
            value={addProjectAddress}
            onChange={(e) => setAddProjectAddress(e.target.value)}
            placeholder="Enter project address"
            fullWidth
          />
        </form>
      </Modal>

      {/* Add/Edit Customer Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Contact Person Information */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center mb-4">
              <UserIcon className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-blue-900" style={{ fontFamily: 'var(--font-family)' }}>
                1. Contact Person Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter contact person name"
                required
                fullWidth
                icon={<UserIcon className="w-5 h-5 text-gray-400" />}
              />
              <Input
                label="Designation"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="e.g., Manager, Director"
                fullWidth
                icon={<FileText className="w-5 h-5 text-gray-400" />}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'var(--font-family)' }}>
                  Phone
                </label>
                <PhoneInput
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="person@example.com"
                fullWidth
                icon={<Mail className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>

          {/* Section 2: Customer Information */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-4">
              <Building2 className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-green-900" style={{ fontFamily: 'var(--font-family)' }}>
                2. Customer Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Company Name"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Enter company name"
                fullWidth
                icon={<Building2 className="w-5 h-5 text-gray-400" />}
              />
              <div className="md:col-span-2">
                <Input
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter full address"
                  fullWidth
                  icon={<MapPin className="w-5 h-5 text-gray-400" />}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'var(--font-family)' }}>
                  Phone
                </label>
                <PhoneInput
                  id="companyPhone"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={(e) => setFormData({ ...formData, companyPhone: e.target.value })}
                  placeholder="Enter company phone"
                />
              </div>
              <Input
                label="Email Address"
                type="email"
                value={formData.companyEmail}
                onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
                placeholder="company@example.com"
                fullWidth
                icon={<Mail className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>

          {/* Section 3: TAX & VAT Information */}
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center mb-4">
              <FileText className="w-5 h-5 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'var(--font-family)' }}>
                3. TAX & VAT Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="TIN No."
                value={formData.taxNumber}
                onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                placeholder="Enter Tax Identification Number"
                fullWidth
                icon={<FileText className="w-5 h-5 text-gray-400" />}
              />
              <Input
                label="BIN No."
                value={formData.bin}
                onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                placeholder="Enter Business Identification Number"
                fullWidth
                icon={<FileText className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>

          {/* Section 4: Projects */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Building2 className="w-5 h-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-orange-900" style={{ fontFamily: 'var(--font-family)' }}>
                  4. Projects
                </h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddProject}
                icon={<Plus className="w-4 h-4" />}
              >
                Add Project
              </Button>
            </div>
            {formData.projects.length === 0 ? (
              <p className="text-sm text-gray-500 italic" style={{ fontFamily: 'var(--font-family)' }}>
                No projects added yet. Click "Add Project" to add one.
              </p>
            ) : (
              <div className="space-y-4">
                {formData.projects.map((project, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-orange-300">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'var(--font-family)' }}>
                        Project #{index + 1}
                      </h4>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveProject(index)}
                        icon={<Trash2 className="w-4 h-4" />}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Project Name"
                        value={project.name}
                        onChange={(e) => handleProjectChange(index, 'name', e.target.value)}
                        placeholder="Enter project name"
                        fullWidth
                        icon={<FileText className="w-5 h-5 text-gray-400" />}
                      />
                      <Input
                        label="Project Address"
                        value={project.address}
                        onChange={(e) => handleProjectChange(index, 'address', e.target.value)}
                        placeholder="Enter project address"
                        fullWidth
                        icon={<MapPin className="w-5 h-5 text-gray-400" />}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              {editingCustomer ? 'Update Customer' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomersPage;
